/**
 * Active-brand store (Faz-5 PR-5, roadmap #441, umbrella #421).
 *
 * Owns the user's active-brand SELECTION and the resolved theme payload, and
 * drives the DOM cascade (`applyActiveBrand`). Only the selected `brandId` is
 * persisted (localStorage) — the resolved `designTokens` are re-fetched from the
 * server on boot so a stale snapshot can never be themed from cache (the server
 * is the source of truth; ownership + active-DS are re-checked every load).
 *
 * Cascade ownership: the store is the SINGLE place that calls `applyActiveBrand`
 * — selecting a brand, clearing it, and boot-init all route through it so the
 * "reset-to-default then layer brand" rule (cascade "A") is applied uniformly.
 *
 * Graceful degradation: a failed resolve/list (unauth, network, 403) never
 * throws to the UI — the store keeps the PR-3 default tokens and an empty/owned
 * list (the selector hides itself), so theming failure is silent + non-fatal.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { activeBrandService, type OwnedBrand } from '../services/activeBrand.service.js';
import { applyActiveBrand, type ActiveBrand } from '../services/uiSystem.service.js';

interface ActiveBrandState {
  /** The selected brand id (persisted). `null` = explicit "no brand" / default. */
  selectedBrandId: number | null;
  /** The resolved active brand + tokens (NOT persisted; re-fetched on boot). */
  active: ActiveBrand | null;
  /** The brands the user owns (BrandSelector options; not persisted). */
  owned: OwnedBrand[];
  /** In-flight guard for the selector (disable while resolving). */
  loading: boolean;

  /** Fetch the owned-brand list (selector options). Silent on failure. */
  loadOwnedBrands: () => Promise<void>;
  /**
   * Select a brand (or `null` to clear). Resolves the theme, applies the cascade,
   * and persists the selection. A 403/failure clears to defaults rather than
   * throwing (the UI never offers a non-owned brand; this is defensive).
   */
  selectBrand: (brandId: number | null) => Promise<void>;
  /**
   * Boot init: resolve the persisted selection (or the server default when none
   * is stored) and apply it. Called once after auth. Silent on failure.
   */
  init: () => Promise<void>;
  /** Reset to defaults (e.g. on logout) — clears selection + restores PR-3 tokens. */
  reset: () => void;
}

export const useActiveBrandStore = create<ActiveBrandState>()(
  persist(
    (set, get) => ({
      selectedBrandId: null,
      active: null,
      owned: [],
      loading: false,

      loadOwnedBrands: async () => {
        try {
          const owned = await activeBrandService.listOwnedBrands();
          set({ owned });
        } catch {
          set({ owned: [] }); // unauth / network → no selector
        }
      },

      selectBrand: async (brandId) => {
        set({ loading: true });
        try {
          const active =
            brandId === null ? null : await activeBrandService.getActiveBrand(brandId);
          await applyActiveBrand(active);
          set({ selectedBrandId: brandId, active, loading: false });
        } catch {
          // 403 (non-owned) / network — fall back to defaults, keep no selection.
          await applyActiveBrand(null);
          set({ selectedBrandId: null, active: null, loading: false });
        }
      },

      init: async () => {
        const { selectedBrandId } = get();
        set({ loading: true });
        try {
          // Re-resolve from the server: persisted id when present, else default.
          const active = await activeBrandService.getActiveBrand(
            selectedBrandId ?? undefined,
          );
          await applyActiveBrand(active);
          // Reconcile the persisted id with what actually resolved (e.g. the brand
          // was un-themed / un-owned since last visit → resolver returns default).
          set({ selectedBrandId: active?.brandId ?? null, active, loading: false });
        } catch {
          await applyActiveBrand(null);
          set({ active: null, loading: false });
        }
      },

      reset: () => {
        void applyActiveBrand(null);
        set({ selectedBrandId: null, active: null, owned: [] });
      },
    }),
    {
      name: 'elmes-active-brand',
      // Persist ONLY the selection — never the resolved tokens (server is SoT).
      partialize: (state) => ({ selectedBrandId: state.selectedBrandId }),
    },
  ),
);
